import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

import { useLogEntryStore } from '@/stores/log-entry-store';
import { useWaypointStore } from '@/stores/waypoint-store';
import { usePropertyStore } from '@/stores/property-store';
import { Button } from '@/components/common/Button';
import { DataCockpit } from '@/components/common/DataCockpit';
import { HuntRatingBar } from '@/components/common/HuntRatingBar';
import { MoonPhaseDisplay } from '@/components/conditions/MoonPhaseDisplay';
import { HuntWindowDisplay } from '@/components/conditions/HuntWindowDisplay';
import { PhotoGallery } from '@/components/photos/PhotoGallery';
import { TagChip } from '@/components/scouting/TagChip';
import { formatTagLabel } from '@/utils/format-utils';
import { formatAnimalSummary } from '@/utils/scouting-utils';
import { getPhotosForEntry } from '@/services/photo-service';
import type { PhotoRecord } from '@/types/photo';
import type { MoonPhaseName } from '@/services/celestial-service';
import type { DataCockpitCell } from '@/types/data-cockpit';

interface DetailRowProps {
  label: string;
  children: React.ReactNode;
  testId?: string;
}

function DetailRow({ label, children, testId }: DetailRowProps) {
  return (
    <div data-testid={testId}>
      <p className="field-label mb-0.5">
        {label}
      </p>
      <div className="text-body-sm text-[var(--color-text-primary)]">{children}</div>
    </div>
  );
}

function formatTemp(temp: number | undefined): string {
  if (temp === undefined) return '—';
  return `${Math.round(temp)}°F`;
}

function formatWind(speed: number | undefined, direction: string | undefined): string {
  if (speed === undefined) return '—';
  return direction ? `${Math.round(speed)} mph ${direction}` : `${Math.round(speed)} mph`;
}


export function LogEntryDetailPage() {
  const { waypointId, entryId } = useParams<{ waypointId: string; entryId: string }>();
  const navigate = useNavigate();

  const { entries, loadEntriesByWaypoint, deleteEntry } = useLogEntryStore();
  const { waypoints: locations, loadWaypoints: loadLocations } = useWaypointStore();
  const { properties, loadProperties } = usePropertyStore();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [photos, setPhotos] = useState<PhotoRecord[]>([]);

  useEffect(() => {
    if (waypointId && entries.length === 0) void loadEntriesByWaypoint(waypointId);
  }, [waypointId, entries.length, loadEntriesByWaypoint]);

  // Load photos when entry is available
  useEffect(() => {
    if (!entryId) return;
    getPhotosForEntry(entryId)
      .then(setPhotos)
      .catch((err) => console.error('[LogEntryDetailPage] Failed to load photos:', err));
  }, [entryId]);

  useEffect(() => {
    if (properties.length === 0) void loadProperties();
  }, [properties.length, loadProperties]);

  const entry = entries.find((e) => e.id === entryId);

  const location = entry ? locations.find((l) => l.id === entry.waypointId) : undefined;
  const property = location ? properties.find((p) => p.id === location.propertyId) : undefined;

  // Load waypoints for breadcrumb once we know the entry's propertyId
  useEffect(() => {
    if (!entry || locations.length > 0) return;
    if (entry.propertyId) void loadLocations(entry.propertyId);
  }, [entry, locations.length, loadLocations]);

  if (!waypointId || !entryId) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-[var(--color-text-muted)] text-body-sm">Invalid URL.</p>
        <Link
          to="/properties"
          className="text-[var(--color-accent)] text-body-sm hover:text-[var(--color-accent-hover)] transition-colors"
        >
          ← Properties
        </Link>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-[var(--color-text-muted)] text-body-sm">Entry not found.</p>
        <Link
          to={`/waypoints/${waypointId}/log`}
          className="text-[var(--color-accent)] text-body-sm hover:text-[var(--color-accent-hover)] transition-colors"
        >
          ← Scouting Log
        </Link>
      </div>
    );
  }

  async function handleDelete() {
    if (!entry) return;
    setIsDeleting(true);
    try {
      await deleteEntry(entry.id);
      navigate(-1);
    } catch {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  const formattedDate = format(parseISO(entry.date), 'MMMM d, yyyy');
  const animalSummary = formatAnimalSummary(entry);

  // Conditions Snapshot: build DataCockpit cells
  const hasConditions =
    entry.weatherTemp !== undefined ||
    entry.windSpeed !== undefined ||
    entry.civilDawnTime !== undefined ||
    entry.barometricPressure !== undefined;

  const cockpitCells: DataCockpitCell[] = [
    {
      primary: formatTemp(entry.weatherTemp),
      secondary: entry.weatherFeelsLike !== undefined ? `Feels ${formatTemp(entry.weatherFeelsLike)}` : undefined,
      label: 'Temp',
      testId: 'cockpit-temp',
    },
    {
      primary: formatWind(entry.windSpeed, entry.windDirection),
      label: 'Wind',
      testId: 'cockpit-wind',
    },
    {
      primary: entry.civilDawnTime
        ? (entry.civilDawnTime.length <= 8 ? entry.civilDawnTime : new Date(entry.civilDawnTime).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }))
        : '—',
      secondary: entry.civilDuskTime
        ? (entry.civilDuskTime.length <= 8 ? entry.civilDuskTime : new Date(entry.civilDuskTime).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }))
        : undefined,
      label: 'Hunt Window',
      testId: 'cockpit-hunt-window',
    },
    {
      primary: entry.barometricPressure !== undefined ? `${entry.barometricPressure.toFixed(2)}` : '—',
      secondary: entry.barometricTrend,
      label: 'Pressure',
      testId: 'cockpit-pressure',
    },
  ];

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      {/* 1. Breadcrumb + header (Date / Time / Location) */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 text-body-xs text-[var(--color-text-muted)]">
          {property && (
            <>
              <Link
                to={`/properties/${property.id}`}
                className="text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] transition-colors"
              >
                {property.name}
              </Link>
              <span aria-hidden="true">›</span>
            </>
          )}
          {location && (
            <>
              <Link
                to={`/land/properties/${property?.id}/waypoints/${location.id}`}
                className="text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] transition-colors"
              >
                {location.name}
              </Link>
              <span aria-hidden="true">›</span>
            </>
          )}
          <Link
            to={`/waypoints/${waypointId}/log`}
            className="text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] transition-colors"
          >
            Scouting Log
          </Link>
        </div>
        <h1 className="font-display text-display-sm text-[var(--color-text-display)] mt-1">
          {formattedDate}
          {entry.time && (
            <span className="text-[var(--color-text-muted)] font-normal text-body-md ml-2">
              {entry.time}
            </span>
          )}
        </h1>
      </div>

      {/* 2. Conditions Snapshot */}
      {hasConditions && (
        <div
          className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4 flex flex-col gap-3"
          data-testid="conditions-snapshot"
        >
          <p className="field-label">
            CONDITIONS SNAPSHOT
          </p>

          {/* Hunt Rating Bar at top */}
          {entry.conditionsQuality != null && (
            <div data-testid="entry-conditions-quality">
              <HuntRatingBar score={entry.conditionsQuality.score} />
            </div>
          )}

          {/* 2x2 Data Cockpit */}
          <DataCockpit cells={cockpitCells} />

          {/* Hunt Window detail (if available) — compact format */}
          {entry.civilDawnTime && entry.sunriseTime && entry.sunsetTime && entry.civilDuskTime && (
            <div className="text-body-xs text-[var(--color-text-muted)]">
              <HuntWindowDisplay
                civilDawn={entry.civilDawnTime}
                sunrise={entry.sunriseTime}
                sunset={entry.sunsetTime}
                civilDusk={entry.civilDuskTime}
                compact
              />
            </div>
          )}

          {/* Moon line — informational only, shown when data is present */}
          {entry.moonPhase && entry.moonIllumination !== undefined && (
            <MoonPhaseDisplay
              phase={entry.moonPhase as MoonPhaseName}
              illumination={entry.moonIllumination}
              rise={null}
              set={null}
              compact
            />
          )}
        </div>
      )}

      {/* Main card */}
      <div
        className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4 flex flex-col gap-4"
        data-testid="entry-detail-card"
      >
        {/* 3. Notes */}
        {entry.notes && (
          <DetailRow label="Notes" testId="notes-section">
            <p className="leading-relaxed whitespace-pre-wrap">{entry.notes}</p>
          </DetailRow>
        )}

        {/* 4. Tags (Inked Stamp style) */}
        {entry.tags.length > 0 && (
          <DetailRow label="Tags">
            <div className="flex flex-wrap gap-1.5 mt-1">
              {entry.tags.map((tag) => (
                <TagChip key={tag} label={formatTagLabel(tag)} state="display" />
              ))}
            </div>
          </DetailRow>
        )}

        {/* 5. Animal Observation */}
        {animalSummary && (
          <DetailRow label="Animal Observation">
            <p>{animalSummary}</p>
          </DetailRow>
        )}

        {/* 6. Photos */}
        {photos.length > 0 && (
          <DetailRow label="Photos">
            <div className="mt-1" data-testid="entry-photos">
              <PhotoGallery photos={photos} />
            </div>
          </DetailRow>
        )}
      </div>

      {/* 7. Action buttons */}
      <div className="flex flex-wrap gap-2 w-full max-w-full" data-testid="log-entry-actions">
        <Link to={`/waypoints/${waypointId}/log/${entry.id}/edit`} className="flex-1 min-w-0">
          <Button variant="ghost" className="w-full" data-testid="edit-button">
            Edit
          </Button>
        </Link>
        <Button
          variant="danger"
          onClick={() => setShowDeleteConfirm(true)}
          className="flex-1 min-w-0"
          data-testid="delete-button"
        >
          Delete
        </Button>
      </div>

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-delete-entry-title"
        >
          <div
            className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-6 max-w-sm w-full shadow-lg"
            data-testid="delete-dialog"
          >
            <h2
              id="confirm-delete-entry-title"
              className="font-display text-display-sm text-[var(--color-text-display)] mb-2"
            >
              Delete Entry?
            </h2>
            <p className="text-body-sm text-[var(--color-text-secondary)] mb-5">
              This scouting entry will be permanently deleted. This cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => void handleDelete()}
                disabled={isDeleting}
                data-testid="confirm-delete-button"
              >
                {isDeleting ? 'Deleting\u2026' : 'Delete Entry'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
